import {
  readAnalyticsConsent,
  subscribeToAnalyticsConsent,
  writeAnalyticsConsent,
  type AnalyticsConsent,
} from "@rental-game/analytics";

export const ANALYTICS_CONSENT_EVENT = "marketing:analytics-consent";

export function initializeConsent(document: Document, view: Window): () => void {
  const panel = document.getElementById("analytics-consent");
  const accept = document.getElementById("analytics-accept") as HTMLButtonElement | null;
  const reject = document.getElementById("analytics-reject") as HTMLButtonElement | null;
  const withdraw = document.getElementById("analytics-withdraw") as HTMLButtonElement | null;
  const settings = document.getElementById("privacy-settings") as HTMLButtonElement | null;
  const status = document.getElementById("analytics-consent-status");
  if (!panel || !accept || !reject || !withdraw || !settings) return () => undefined;
  let disposed = false;
  let state = readAnalyticsConsent(document, view.location);

  const announce = (): void => {
    view.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: { state } }));
  };
  const render = (showPanel = state === "unknown"): void => {
    document.documentElement.dataset.analyticsConsent = state;
    panel.hidden = !showPanel;
    panel.setAttribute("aria-hidden", String(!showPanel));
    withdraw.hidden = state !== "granted";
    accept.hidden = state === "granted";
    reject.hidden = state === "denied";
    if (status)
      status.textContent =
        state === "granted"
          ? "Analytics is on. You can withdraw consent at any time."
          : state === "denied"
            ? "Analytics is off. You can allow it at any time."
            : "Analytics is off until you choose Allow analytics.";
  };
  const choose = (next: Exclude<AnalyticsConsent, "unknown">): void => {
    if (disposed) return;
    const persisted = writeAnalyticsConsent(document, view.location, next);
    state = next === "granted" && !persisted ? "denied" : next;
    render(next === "granted" && !persisted);
    if (next === "granted" && !persisted && status)
      status.textContent = "Your choice could not be saved, so analytics remains off.";
    announce();
  };
  const reconcile = (): void => {
    if (disposed) return;
    const next = readAnalyticsConsent(document, view.location);
    if (next === state) return;
    state = next;
    render();
    announce();
  };
  const showSettings = (): void => {
    if (disposed) return;
    render(true);
    (state === "granted" ? withdraw : accept).focus();
  };
  const allow = (): void => choose("granted");
  const deny = (): void => choose("denied");
  accept.addEventListener("click", allow);
  reject.addEventListener("click", deny);
  withdraw.addEventListener("click", deny);
  settings.addEventListener("click", showSettings);
  const unsubscribe = subscribeToAnalyticsConsent(view, reconcile);
  render();
  announce();

  return () => {
    disposed = true;
    unsubscribe();
    accept.removeEventListener("click", allow);
    reject.removeEventListener("click", deny);
    withdraw.removeEventListener("click", deny);
    settings.removeEventListener("click", showSettings);
    delete document.documentElement.dataset.analyticsConsent;
  };
}
